/**
 * preview.js — the real page, with the real stamp, before anything is written.
 *
 * WHAT WAS HERE BEFORE. A CSS mock-up: a grey rectangle with two grey bars
 * standing in for "a document", and one absolutely-positioned <div> standing in
 * for the watermark. It could not show tiling (the tile option drew a single
 * centred stamp, exactly like diagonal), could not show the margin logic, could
 * not show what the stamp does to the text underneath, and showed nothing at
 * all about the page the visitor had just handed over. pdf.js was already
 * loaded on the page — to count the pages, and for nothing else.
 *
 * So this renders the page. Once per page, into a cached canvas; every slider
 * move only recomposites, which is why dragging opacity stays smooth on a
 * 300-page file.
 *
 * THE LAYER IS COMPOSITED, NOT DESCRIBED. "Under the content" is not a promise
 * that the watermark stays visible — in a PDF whose own content paints an
 * opaque background it is completely hidden, and that is true of every tool
 * offering the option. Rather than explain that in a FAQ nobody reads, the page
 * is rendered on a TRANSPARENT canvas and drawn OVER the stamp in the same
 * order the PDF will paint them. If the stamp disappears here, it disappears in
 * the file — and `onCoverage` lets the interface say so before the click.
 *
 * Placement is not computed here. It comes from add-watermark/layout.js, the
 * same file the worker uses; this only turns PDF space (y up) into canvas space
 * (y down).
 *
 * Exposed as window.WmPreview.
 */
(function (root) {
    'use strict';
    if (root.WmPreview) return;

    var CAP_RATIO = 0.72;   // must equal the worker's constant

    function fontCss(key, px) {
        var bold = /bold/.test(key || '') ? '700' : '400';
        var fam = 'Helvetica, Arial, sans-serif';
        if (/^times/.test(key || '')) fam = '"Times New Roman", Times, serif';
        else if (/^courier/.test(key || '')) fam = '"Courier New", Courier, monospace';
        return bold + ' ' + px + 'px ' + fam;
    }

    function create(opts) {
        var canvas = opts.canvas;
        var getState = opts.getState;
        var ctx = canvas.getContext('2d');

        var doc = null;             // pdf.js document
        var pageIndex = 0;          // 0-based
        var pageCanvas = null;      // the rendered page, transparent background
        var pageBox = { w: 595, h: 842 };   // PDF points
        var renderTask = null;
        var renderToken = 0;
        var coverage = 0;           // share of fully opaque pixels on this page
        var cssW = 0;
        var destroyed = false;

        // -- rendering the page ------------------------------------------

        function displayWidth() {
            var w = canvas.clientWidth
                || (canvas.parentNode && canvas.parentNode.clientWidth)
                || 320;
            return Math.max(120, Math.round(w));
        }

        async function renderPage() {
            if (!doc || destroyed) return;
            var token = ++renderToken;
            var page;
            try { page = await doc.getPage(pageIndex + 1); } catch (e) { return; }
            if (token !== renderToken) { try { page.cleanup(); } catch (e) {} return; }

            var base = page.getViewport({ scale: 1 });
            pageBox = { w: base.width, h: base.height };

            var dpr = Math.min(2.5, root.devicePixelRatio || 1);
            var w = displayWidth();
            cssW = w;
            var vp = page.getViewport({ scale: (w * dpr) / base.width });

            var c = root.document.createElement('canvas');
            c.width = Math.max(1, Math.round(vp.width));
            c.height = Math.max(1, Math.round(vp.height));
            var cc = c.getContext('2d');

            try { if (renderTask) renderTask.cancel(); } catch (e) {}
            try {
                // Transparent, so the layer order below is the PDF's own.
                renderTask = page.render({ canvasContext: cc, viewport: vp, background: 'rgba(0,0,0,0)' });
                await renderTask.promise;
            } catch (e) {
                renderTask = null;
                try { page.cleanup(); } catch (e2) {}
                if (token === renderToken) { pageCanvas = null; paint(); }
                return;
            }
            renderTask = null;
            try { page.cleanup(); } catch (e) {}
            if (token !== renderToken || destroyed) return;

            pageCanvas = c;
            coverage = measureCoverage(cc, c.width, c.height);
            if (opts.onCoverage) opts.onCoverage(coverage);
            paint();
        }

        /**
         * How much of the page paints something fully opaque.
         *
         * Sampled on a grid rather than read whole: a 900x1270 page is 1.1 M
         * pixels and this runs on every page turn. 40x56 samples answer "does
         * this page have a solid background" at no measurable cost.
         */
        function measureCoverage(cc, w, h) {
            try {
                var cols = 40, rows = 56, opaque = 0, total = 0;
                for (var j = 0; j < rows; j++) {
                    var y = Math.min(h - 1, Math.floor((j + 0.5) * h / rows));
                    var strip = cc.getImageData(0, y, w, 1).data;
                    for (var i = 0; i < cols; i++) {
                        var x = Math.min(w - 1, Math.floor((i + 0.5) * w / cols));
                        total++;
                        if (strip[x * 4 + 3] > 250) opaque++;
                    }
                }
                return total ? opaque / total : 0;
            } catch (e) { return 0; }
        }

        // -- the stamp ---------------------------------------------------

        /** The unrotated stamp box in PDF points, for the current settings. */
        function stampBox(s) {
            if (s.mode === 'image') {
                if (!s.image || !s.image.width) return null;
                var w = pageBox.w * (Math.max(5, Math.min(100, s.imageWidthPct)) / 100);
                return { w: w, h: w * (s.image.height / s.image.width) };
            }
            var text = s.text || '';
            if (!text) return null;
            ctx.save();
            ctx.font = fontCss(s.font, s.size);
            var tw = ctx.measureText(text).width;
            ctx.restore();
            return { w: Math.max(1, tw), h: s.size * CAP_RATIO };
        }

        function placements(s) {
            var box = stampBox(s);
            if (!box || !root.WmLayout) return { box: null, spots: [] };
            return {
                box: box,
                spots: root.WmLayout.places({
                    pageW: pageBox.w, pageH: pageBox.h,
                    stampW: box.w, stampH: box.h,
                    position: s.position, freeX: s.freeX, freeY: s.freeY,
                    rotation: s.rotation,
                    tile: s.tile, tileDensity: s.tileDensity,
                    margin: 36,
                }),
            };
        }

        function drawStamps(g, s, k) {
            var p = placements(s);
            if (!p.box) return;
            g.save();
            g.globalAlpha = Math.max(0.05, Math.min(1, (s.opacity || 30) / 100));
            for (var i = 0; i < p.spots.length; i++) {
                var sp = p.spots[i];
                g.save();
                // PDF space is y-up, canvas is y-down. Translating to the anchor
                // and flipping the rotation sign is the whole conversion.
                g.translate(sp.x * k, (pageBox.h - sp.y) * k);
                g.rotate(-sp.rot * Math.PI / 180);
                if (s.mode === 'image' && s.image) {
                    g.drawImage(s.image, 0, -p.box.h * k, p.box.w * k, p.box.h * k);
                } else {
                    g.font = fontCss(s.font, s.size * k);
                    g.fillStyle = s.color || '#999999';
                    g.textBaseline = 'alphabetic';
                    g.textAlign = 'left';
                    g.fillText(s.text || '', 0, 0);
                }
                g.restore();
            }
            g.restore();
        }

        // -- compositing -------------------------------------------------

        function paint() {
            if (destroyed) return;
            var s = getState();
            var dpr = Math.min(2.5, root.devicePixelRatio || 1);
            var w = displayWidth();
            var h = Math.round(w * (pageBox.h / pageBox.w));

            canvas.width = Math.max(1, Math.round(w * dpr));
            canvas.height = Math.max(1, Math.round(h * dpr));
            canvas.style.height = h + 'px';

            var k = canvas.width / pageBox.w;   // PDF points -> device pixels

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            // Paper. The page itself may or may not paint one, which is the
            // whole point of the layer preview, so the white lives underneath.
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            if (s.layer === 'under') drawStamps(ctx, s, k);
            if (pageCanvas) ctx.drawImage(pageCanvas, 0, 0, canvas.width, canvas.height);
            if (s.layer !== 'under') drawStamps(ctx, s, k);
        }

        // -- dragging ----------------------------------------------------

        var GRID = ['top-left', 'top-center', 'top-right',
                    'middle-left', 'middle-center', 'middle-right',
                    'bottom-left', 'bottom-center', 'bottom-right'];

        /** Fractional centre of each of the nine cells, for snapping. */
        function gridFractions(box, rotation) {
            var b = root.WmLayout.bbox(box.w, box.h, rotation);
            var out = {};
            GRID.forEach(function (pos) {
                var c = root.WmLayout._gridCentre(pos, pageBox.w, pageBox.h, b.w, b.h, 36);
                out[pos] = { fx: c.cx / pageBox.w, fy: c.cy / pageBox.h };
            });
            return out;
        }

        var dragging = false;

        function pointTo(ev) {
            var r = canvas.getBoundingClientRect();
            var fx = (ev.clientX - r.left) / Math.max(1, r.width);
            var fy = 1 - (ev.clientY - r.top) / Math.max(1, r.height);
            return { fx: Math.max(0, Math.min(1, fx)), fy: Math.max(0, Math.min(1, fy)) };
        }

        function place(ev) {
            var s = getState();
            if (s.tile) return;                 // a tiled pattern has no one place
            var box = stampBox(s);
            if (!box) return;
            var p = pointTo(ev);

            // Snap back onto a grid cell when the drop lands near one: the nine
            // buttons and the drag are the SAME setting, so each must be able to
            // reach the other's values or the picker goes stale after one drag.
            var snapped = null, best = 1e9;
            var fr = gridFractions(box, s.rotation);
            var aspect = pageBox.h / pageBox.w;
            GRID.forEach(function (pos) {
                var dx = fr[pos].fx - p.fx;
                var dy = (fr[pos].fy - p.fy) * aspect;
                var d = Math.sqrt(dx * dx + dy * dy);
                if (d < best) { best = d; snapped = pos; }
            });
            if (best > 0.045) snapped = null;

            if (opts.onPlace) opts.onPlace(p.fx, p.fy, snapped);
        }

        canvas.addEventListener('pointerdown', function (ev) {
            var s = getState();
            if (s.tile || !s.ready) return;
            dragging = true;
            try { canvas.setPointerCapture(ev.pointerId); } catch (e) {}
            canvas.classList.add('is-dragging');
            place(ev);
            ev.preventDefault();
        });
        canvas.addEventListener('pointermove', function (ev) {
            if (!dragging) return;
            place(ev);
            ev.preventDefault();
        });
        function endDrag(ev) {
            if (!dragging) return;
            dragging = false;
            canvas.classList.remove('is-dragging');
            try { canvas.releasePointerCapture(ev.pointerId); } catch (e) {}
        }
        canvas.addEventListener('pointerup', endDrag);
        canvas.addEventListener('pointercancel', endDrag);

        // -- resize ------------------------------------------------------

        var resizeTimer = null;
        function onResize() {
            if (Math.abs(displayWidth() - cssW) < 12) return;
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(renderPage, 120);
        }
        var ro = null;
        if (root.ResizeObserver) {
            ro = new root.ResizeObserver(onResize);
            try { ro.observe(canvas.parentNode || canvas); } catch (e) {}
        } else {
            root.addEventListener('resize', onResize);
        }

        return {
            setDocument: function (d) {
                doc = d;
                pageIndex = 0;
                pageCanvas = null;
                return renderPage();
            },
            setPage: function (i) {
                if (!doc) return;
                var n = doc.numPages || 1;
                pageIndex = Math.max(0, Math.min(n - 1, i));
                pageCanvas = null;
                paint();
                return renderPage();
            },
            page: function () { return pageIndex; },
            pages: function () { return doc ? doc.numPages : 0; },
            coverage: function () { return coverage; },
            pageSize: function () { return { w: pageBox.w, h: pageBox.h }; },
            /** The REAL placements for the current settings — what the worker
                will be asked to draw. Used by the browser test, which would
                otherwise be measuring a stand-in stamp instead of this one. */
            placements: function () { return placements(getState()); },
            repaint: paint,
            destroy: function () {
                destroyed = true;
                try { if (renderTask) renderTask.cancel(); } catch (e) {}
                if (ro) { try { ro.disconnect(); } catch (e) {} }
            },
        };
    }

    root.WmPreview = { create: create, _fontCss: fontCss };
})(window);
